"use client";

import { Suspense, useState } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { Building2 } from "lucide-react";
import { login } from "@/lib/auth-api";
import { useUser } from "@/lib/user-context";

function HospitalLoginForm() {
  const search = useSearchParams();
  const redirect = search.get("redirect") || "/hospital/dashboard";
  const { refresh } = useUser();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await login(email, password);
      await refresh();
      // 병원 관리자 타입 확인
      if (result?.user?.user_type !== "hospital") {
        setError("병원 관리자 계정이 아닙니다. 일반 회원은 일반 로그인을 이용해주세요.");
        setLoading(false);
        return;
      }
      window.location.href = redirect;
    } catch (err) {
      setError(err instanceof Error ? err.message : "로그인 실패");
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div>
        <label className="text-muted-foreground mb-1.5 block text-xs font-semibold">이메일</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="hospital@example.com"
          className="w-full rounded-xl border border-gray-200 px-4 py-3 text-base outline-none focus:border-blue-500"
        />
      </div>

      <div>
        <label className="text-muted-foreground mb-1.5 block text-xs font-semibold">비밀번호</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          placeholder="••••••••"
          className="w-full rounded-xl border border-gray-200 px-4 py-3 text-base outline-none focus:border-blue-500"
        />
      </div>

      {error && <div className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>}

      <button
        type="submit"
        disabled={loading}
        className="mt-2 rounded-full bg-blue-600 py-3.5 text-base font-bold text-white shadow-lg shadow-blue-600/15 transition-all hover:-translate-y-0.5 hover:bg-blue-700 disabled:opacity-50"
      >
        {loading ? "로그인 중..." : "병원 관리자 로그인"}
      </button>
    </form>
  );
}

export default function HospitalLoginPage() {
  return (
    <div className="mx-auto max-w-sm px-5 py-12">
      {/* 병원 관리자 아이콘 */}
      <div className="mb-4 flex justify-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-blue-100 bg-blue-50">
          <Building2 className="h-7 w-7 text-blue-600" />
        </div>
      </div>

      <h1 className="mb-2 text-center text-3xl font-extrabold tracking-tight">병원 관리자</h1>
      <p className="text-muted-foreground mb-8 text-center text-sm">
        인증된 병원 관리자 계정으로 로그인하세요
      </p>

      <Suspense fallback={<div className="text-muted-foreground text-center text-sm">로딩...</div>}>
        <HospitalLoginForm />
      </Suspense>

      {/* 하단 링크 */}
      <div className="mt-8 space-y-3 text-center">
        <p className="text-muted-foreground text-xs">
          아직 병원 인증을 하지 않으셨나요?{" "}
          <Link href="/hospital/claim" className="font-semibold text-blue-600">
            병원 인증 신청
          </Link>
        </p>
        <p className="text-muted-foreground text-xs">
          일반 회원이신가요?{" "}
          <Link href="/signin" className="font-semibold text-violet-600">
            일반 로그인
          </Link>
        </p>
      </div>
    </div>
  );
}
